/**
 * Shared utilities for review rating UI (objective and behavioural measures).
 * Used by self-review, manager review, and peer review flows.
 */

/** Measure-like object with level criteria (camelCase or PascalCase from API). */
export type MeasureWithLevelCriteria = Record<string, string | undefined>;

/**
 * Get level criteria text from a measure. Handles camelCase or PascalCase from API.
 */
export function getLevelCriteria(
  measure: MeasureWithLevelCriteria | undefined,
  level: 1 | 2 | 3
): string {
  if (!measure) return '—';
  const camel =
    level === 1 ? 'level1Criteria' : level === 2 ? 'level2Criteria' : 'level3Criteria';
  const pascal =
    level === 1 ? 'Level1Criteria' : level === 2 ? 'Level2Criteria' : 'Level3Criteria';
  const value = measure[camel] ?? measure[pascal];
  return value && String(value).trim() ? value : '—';
}

/**
 * Normalise rating from API (number or enum string) to 1 | 2 | 3.
 * Returns undefined for unset or invalid; 0 is treated as N/A (caller handles separately).
 */
export function normaliseRating(
  value: number | string | undefined
): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && (value === 1 || value === 2 || value === 3))
    return value;
  if (typeof value === 'string') {
    const map: Record<string, number> = {
      DevelopmentRequired: 1,
      MeetsExpectation: 2,
      ExceedsExpectation: 3,
    };
    return map[value] ?? undefined;
  }
  return undefined;
}

/**
 * MUI color for a rating value (e.g. chip or text).
 */
export function getRatingColor(
  rating?: number
): 'text.secondary' | 'success.main' | 'warning.main' | 'error.main' {
  if (!rating) return 'text.secondary';
  if (rating === 3) return 'success.main';
  if (rating === 2) return 'warning.main';
  return 'error.main';
}
